import { cn } from '../../utils/cn';

/**
 * Unified Badge Component
 * 
 * Consistent badge/pill styling with optional glass morphism.
 * 
 * @param {string} variant - Badge style: 'default' | 'primary' | 'success' | 'warning' | 'error'
 * @param {boolean} glass - Apply glass morphism effect
 * @param {string} size - Size: 'sm' | 'md' | 'lg'
 * @param {string} className - Additional classes
 * 
 * Usage:
 * <Badge>Default</Badge>
 * <Badge variant="primary">Primary</Badge>
 * <Badge variant="success" glass>Glass success badge</Badge>
 */
export function Badge({ 
  children, 
  variant = 'default',
  glass = false,
  size = 'md',
  className = '',
  ...props 
}) {
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-xs',
    lg: 'px-4 py-1.5 text-sm',
  };

  const variantClasses = {
    default: 'bg-surface text-text-primary',
    primary: 'bg-primary/20 text-primary',
    success: 'bg-green-100 text-green-700',
    warning: 'bg-amber-100 text-amber-700',
    error: 'bg-red-100 text-red-700',
  };

  const glassClasses = glass ? 'glass-light backdrop-blur-sm' : '';

  return (
    <span
      className={cn(
        'inline-flex items-center font-semibold rounded-full',
        sizeClasses[size],
        variantClasses[variant],
        glassClasses,
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
}
